import { Close, FilterList } from "@mui/icons-material";
import {
  Box,
  ClickAwayListener,
  Divider,
  FormControl,
  Popper,
  Typography,
  useTheme,
} from "@mui/material";
import { PMButton, SearchBar } from "@pimo/pimo-components";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import { useEffect, useMemo, useRef, useState } from "react";

import { ControlledSelectField } from "./pimo-form";

export type FilterData = {
  searchQueryFilter: string[];
  statusFilter: string[];
  completionFilter: string[];
  categoryFilter: string[];
  updateStatusFilter: string[];
};

export type SearchAndFilterProps = {
  language: SupportedLanguage;
  filterData?: FilterData;
  handleChange: (filterData: FilterData) => void;
  clearAllFilters?: () => void;
  options?: {
    status: {
      options?: string[];
      isEnabled?: boolean;
    };
    completion?: {
      options?: string[];
      isEnabled?: boolean;
    };
    category?: {
      options?: string[];
      isEnabled?: boolean;
    };
    updateStatus: {
      options?: string[];
      isEnabled?: boolean;
    };
  };
};

export const SearchAndFilter = ({
  filterData,
  handleChange,
  clearAllFilters,
  options,
  language,
}: SearchAndFilterProps) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);

  const [filterDataState, setFilterDataState] = useState<FilterData>(
    filterData || {
      searchQueryFilter: [],
      statusFilter: [],
      completionFilter: [],
      categoryFilter: [],
      updateStatusFilter: [],
    }
  );

  // Sync external filterData changes into local state during render
  // instead of via a useEffect to avoid cascading renders.
  const [previousFilterData, setPreviousFilterData] = useState(filterData);
  if (filterData && filterData !== previousFilterData) {
    setPreviousFilterData(filterData);
    setFilterDataState(filterData);
  }

  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFilterRef = useRef<FilterData | null>(null);

  const debouncedHandleChange = useMemo(() => {
    return (nextFilterData: FilterData) => {
      pendingFilterRef.current = nextFilterData;

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        if (pendingFilterRef.current) {
          handleChange(pendingFilterRef.current);
        }
        debounceTimeoutRef.current = null;
      }, 500);
    };
  }, [handleChange]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);
  const clickAwayHandler = () => {
    setOpen(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          border: "1px solid",
          borderRadius: "4px",
          borderColor: "#31384980",
          backgroundColor: "#FAFAFA",
          "&:hover": {
            backgroundColor: "#FCFCFC",
          },
        }}
      >
        <SearchBar
          placeholderText={getLocalizedMessage(language, "search") || "Search"}
          onChange={(value) => {
            const updatedFilterData = {
              ...filterDataState,
              searchQueryFilter: [value],
            };
            setFilterDataState(updatedFilterData);
            debouncedHandleChange(updatedFilterData);
          }}
        />
        <Box
          id={"STATUS_BUTTON_ID"}
          data-testid={"STATUS_BUTTON_ID"}
          sx={{
            // Anchor the active-filter dot to this control, not to an ancestor.
            position: "relative",
            color: "#122B54",
            marginRight: 1.5,
            cursor: "pointer",
            verticalAlign: "middle",
            borderRadius: "8px",
            p: 0.5,
            height: "30px",
            width: "30px",
          }}
          onClick={(e) => {
            setOpen(true);
            setAnchorEl(e.currentTarget);
          }}
        >
          <FilterList sx={{ height: "auto", width: "auto" }} />
          {(filterDataState.statusFilter.length > 0 ||
            filterDataState.completionFilter.length > 0 ||
            filterDataState.categoryFilter.length > 0 ||
            filterDataState.updateStatusFilter.length > 0) && (
            <Box
              data-testid={"ACTIVE_FILTER_DOT"}
              sx={{
                height: "7px",
                width: "7px",
                backgroundColor: theme.palette.primary.main,
                borderRadius: "50%",
                position: "absolute",
                top: "6px",
                right: "2px",
              }}
            />
          )}
        </Box>
      </Box>
      {open && (
        <ClickAwayListener
          onClickAway={clickAwayHandler}
          mouseEvent={"onMouseUp"}
        >
          <Popper
            data-testid={"reportingDate-popover"}
            open={open}
            anchorEl={anchorEl}
            disablePortal={false}
            sx={{
              zIndex: theme.zIndex.modal,
              backgroundColor: "background.paper",
              borderColor: theme.palette.primary.main,
              boxShadow: "1px 1px 2px grey",
              borderRadius: "12px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              p: 1,
              maxHeight: "84vh",

              overflow: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  p: 1,
                  gap: 0,
                  width: 300,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "start",
                      width: "100%",
                      pr: 1,
                    }}
                  >
                    <FilterList
                      sx={{ color: theme.palette.primary.main, height: "35px" }}
                    />
                    <Typography sx={{ fontWeight: 500 }}>Filter</Typography>
                  </Box>
                  <Close
                    sx={{ ":hover": { cursor: "pointer" } }}
                    onClick={clickAwayHandler}
                  ></Close>
                </Box>
                <Divider
                  sx={{
                    bgcolor: theme.palette.secondary.main,
                    height: "1px",
                    borderWidth: 0,
                  }}
                />
                <FormControl
                  component="fieldset"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    marginY: 2,
                    width: "100%",
                  }}
                >
                  {options?.status?.isEnabled && (
                    <ControlledSelectField
                      options={options?.status?.options ?? []}
                      value={filterDataState?.statusFilter ?? []}
                      label={
                        getLocalizedMessage(language, "status") || "Status"
                      }
                      multiple={true}
                      onChange={(newValue) => {
                        let result = [];
                        if (Array.isArray(newValue)) {
                          result = newValue as string[];
                        } else {
                          result = [newValue as string];
                        }
                        const updatedFilterData = {
                          ...filterDataState,
                          statusFilter: result,
                        };
                        setFilterDataState(updatedFilterData);
                        debouncedHandleChange(updatedFilterData);
                      }}
                    ></ControlledSelectField>
                  )}

                  {options?.completion?.isEnabled && (
                    <ControlledSelectField
                      options={options?.completion?.options ?? []}
                      value={filterDataState?.completionFilter ?? []}
                      label={
                        getLocalizedMessage(language, "completion") ||
                        "Completion"
                      }
                      multiple={true}
                      onChange={(newValue) => {
                        let result = [];
                        if (Array.isArray(newValue)) {
                          result = newValue as string[];
                        } else {
                          result = [newValue as string];
                        }
                        const updatedFilterData = {
                          ...filterDataState,
                          completionFilter: result,
                        };
                        setFilterDataState(updatedFilterData);
                        debouncedHandleChange(updatedFilterData);
                      }}
                    ></ControlledSelectField>
                  )}
                  {options?.category?.isEnabled && (
                    <ControlledSelectField
                      options={options?.category?.options ?? []}
                      value={filterDataState?.categoryFilter ?? []}
                      label={
                        getLocalizedMessage(language, "category") || "Category"
                      }
                      multiple={true}
                      onChange={(newValue) => {
                        let result = [];
                        if (Array.isArray(newValue)) {
                          result = newValue as string[];
                        } else {
                          result = [newValue as string];
                        }
                        const updatedFilterData = {
                          ...filterDataState,
                          categoryFilter: result,
                        };
                        setFilterDataState(updatedFilterData);
                        debouncedHandleChange(updatedFilterData);
                      }}
                    ></ControlledSelectField>
                  )}
                </FormControl>
              </Box>
              <PMButton
                size={"medium"}
                variant={"contained"}
                text={getLocalizedMessage(language, "clearAll") || "Clear All"}
                onClick={clearAllFilters}
              ></PMButton>
            </Box>
          </Popper>
        </ClickAwayListener>
      )}
    </Box>
  );
};
